import { useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { AnnouncementDialog } from '@/components/AnnouncementDialog';
import { AnnouncementPicture, useAnnouncementPicture } from '@/components/AnnouncementPicture';
import { NoticeMeta, PriorityTag } from '@/components/AnnouncementParts';
import { AppIcon } from '@/components/AppIcon';
import { localize, useAnnouncementFeed, type FeedItem, type Priority } from '@/components/announcementFeed';
import { ErrorNote, errorMessage } from '@/components/ui';
import { useNow } from '@/hooks/useNow';
import { useAuth } from '@/stores/auth';
import { usePreferences } from '@/stores/preferences';
import { useShellNav } from '@/stores/shellNav';

/** Cards under the spotlight before "Show more". */
export const CARDS_BEFORE_MORE = 6;
/** Below this many notices, or with only one kind, filters would be noise. */
const FILTER_FROM = 4;
const MINUTE = 60_000;

type Filter = 'ALL' | Exclude<Priority, 'INFO'>;

interface FilterOption {
  id: Filter;
  label: string;
  count: number;
}

/**
 * The Dashboard's main block: the notices put up for this person, the most
 * pressing one large with its picture, the rest as cards beneath it.
 *
 * The feed arrives already in order — urgent, then important, then the rest,
 * newest first within each — so the spotlight is simply the first notice and
 * nothing here re-sorts. The ticker above reads the same query, and announces
 * a new urgent notice to screen readers; this board does not repeat that.
 */
export function AnnouncementBoard() {
  const can = useAuth((s) => s.can);
  const navigate = useShellNav((s) => s.navigate);
  const language = usePreferences((s) => s.prefs.language);
  const feed = useAnnouncementFeed();
  const now = useNow(MINUTE);

  const [filter, setFilter] = useState<Filter>('ALL');
  const [showAll, setShowAll] = useState(false);
  const [opened, setOpened] = useState<FeedItem | null>(null);

  const items = feed.data ?? [];
  const filters = filtersFor(items);
  // A filter whose last notice has gone falls back to everything. Worked out
  // here rather than in an effect, so no render shows an empty board.
  const active = filters.find((f) => f.id === filter)?.id ?? 'ALL';
  const shown = active === 'ALL' ? items : items.filter((i) => i.priority === active);
  const [spotlight, ...rest] = shown;
  const cards = showAll ? rest : rest.slice(0, CARDS_BEFORE_MORE);

  // The open notice as it is now; undefined once it has left the feed.
  const openedNow = opened ? items.find((i) => i.id === opened.id) : undefined;

  const canManage = can('com.announcement.read');
  const canWrite = canManage && can('com.announcement.create');
  const write = () => navigate('home-announce', { action: 'create' });

  function body() {
    if (feed.isPending) return <BoardSkeleton />;

    if (feed.isError && !feed.data) {
      return (
        <div className="board__problem">
          <ErrorNote message={`Announcements could not be loaded. ${errorMessage(feed.error, '')}`} />
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => void feed.refetch()}
            disabled={feed.isFetching}
          >
            {feed.isFetching ? 'Trying…' : 'Try again'}
          </button>
        </div>
      );
    }

    if (!spotlight) {
      return (
        <div className="board__empty">
          <span className="board__emptyicon" aria-hidden="true">
            <AppIcon kind="announcement" size={24} />
          </span>
          <p className="board__emptytitle">Nothing from the office right now</p>
          <p className="board__emptynote">Notices will show here as soon as they are put up.</p>
          {canWrite && (
            <button type="button" className="btn btn--sm" onClick={write}>
              Write an announcement
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <AnnouncementSpotlight
          key={spotlight.id}
          item={spotlight}
          language={language}
          now={now}
          onOpen={() => setOpened(spotlight)}
        />

        {cards.length > 0 && (
          <ul className="board__grid" aria-label="More announcements">
            {cards.map((item) => (
              <AnnouncementTile
                key={item.id}
                item={item}
                language={language}
                now={now}
                onOpen={() => setOpened(item)}
              />
            ))}
          </ul>
        )}

        {rest.length > CARDS_BEFORE_MORE && (
          <button
            type="button"
            className="btn btn--sm btn--ghost board__more"
            aria-expanded={showAll}
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? 'Show fewer' : `Show ${rest.length - CARDS_BEFORE_MORE} more`}
          </button>
        )}
      </>
    );
  }

  return (
    <section className="board" aria-labelledby="board-title">
      <header className="board__head">
        <h3 id="board-title" className="board__title">
          <span className="board__titleicon" aria-hidden="true">
            <AppIcon kind="announcement" size={17} />
          </span>
          Announcements
          {items.length > 0 && (
            <bdi className="board__count">{items.length} live</bdi>
          )}
        </h3>
        <div className="board__actions">
          {canWrite && (
            <button type="button" className="btn btn--sm" onClick={write}>
              <AppIcon kind="add" size={14} /> New announcement
            </button>
          )}
          {canManage && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => navigate('home-announce')}>
              Manage
            </button>
          )}
        </div>
      </header>

      {filters.length > 0 && (
        <div className="board__filters" role="group" aria-label="Show">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`board__filter board__filter--${f.id.toLowerCase()}`}
              aria-pressed={active === f.id}
              onClick={() => {
                setFilter(f.id);
                setShowAll(false);
              }}
            >
              {f.label} <span className="board__filtercount">{f.count}</span>
            </button>
          ))}
        </div>
      )}

      {body()}

      {opened && (
        <AnnouncementDialog item={openedNow ?? opened} live={openedNow !== undefined} onClose={() => setOpened(null)} />
      )}
    </section>
  );
}

/** The filter buttons worth offering for these notices; none when they would not help. */
function filtersFor(items: readonly FeedItem[]): FilterOption[] {
  const urgent = items.filter((i) => i.priority === 'URGENT').length;
  const important = items.filter((i) => i.priority === 'IMPORTANT').length;
  const kinds = [urgent, important, items.length - urgent - important].filter((n) => n > 0).length;
  if (items.length < FILTER_FROM || kinds < 2) return [];

  const options: FilterOption[] = [{ id: 'ALL', label: 'All', count: items.length }];
  if (urgent > 0) options.push({ id: 'URGENT', label: 'Urgent', count: urgent });
  if (important > 0) options.push({ id: 'IMPORTANT', label: 'Important', count: important });
  return options;
}

/**
 * The one notice shown large. Exported for the composer's preview, so what an
 * author sees before publishing is drawn by the same code people will see.
 */
export function AnnouncementSpotlight({
  item,
  language,
  now,
  onOpen,
  pictureSrc,
  preview = false,
}: {
  item: FeedItem;
  language: string;
  now: number;
  onOpen: () => void;
  /** A picture picked in the composer and not saved yet. */
  pictureSrc?: string | undefined;
  /** In the composer: nothing to open. */
  preview?: boolean;
}) {
  const text = localize(item, language);
  const headingId = useId();
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const clipped = useClipped(bodyRef, text.body);
  const hasPicture = !!(item.imageKey || pictureSrc);

  return (
    <article
      className={`board__spot board__spot--${item.priority.toLowerCase()}${hasPicture ? ' board__spot--picture' : ''}`}
      aria-labelledby={headingId}
    >
      <div className="board__spottext">
        <PriorityTag priority={item.priority} />
        <h4 id={headingId} className="board__spotheadline" dir="auto">
          {text.headline}
        </h4>
        {text.body && (
          <p ref={bodyRef} className="board__spotbody" dir="auto">
            {text.body}
          </p>
        )}
        <NoticeMeta item={item} author={text.author} now={now} />
        {!preview && clipped && (
          <div className="board__spotactions">
            <button type="button" className="btn btn--sm" onClick={onOpen}>
              Read more
            </button>
          </div>
        )}
      </div>

      {hasPicture && (
        <AnnouncementPicture
          id={item.id}
          imageKey={item.imageKey}
          alt={text.headline}
          className="announcementpicture--spot"
          onOpen={preview ? undefined : onOpen}
          src={pictureSrc}
        />
      )}
    </article>
  );
}

/** A smaller notice; the whole card opens it in full. */
function AnnouncementTile({
  item,
  language,
  now,
  onOpen,
}: {
  item: FeedItem;
  language: string;
  now: number;
  onOpen: () => void;
}) {
  const text = localize(item, language);

  return (
    <li className={`board__tile board__tile--${item.priority.toLowerCase()}`}>
      <Thumbnail item={item} />
      <div className="board__tiletext">
        <PriorityTag priority={item.priority} />
        <h4 className="board__tileheadline" dir="auto">
          {/* One real button per card; its ::after stretches over the card,
              so a click anywhere opens it and a keyboard reaches it once. */}
          <button type="button" className="board__tileopen" onClick={onOpen}>
            {text.headline}
            {item.imageKey && (
              <>
                {' '}
                <span className="sr-only">(has a picture)</span>
              </>
            )}
          </button>
        </h4>
        {text.body && (
          <p className="board__tilebody" dir="auto">
            {text.body}
          </p>
        )}
        <NoticeMeta item={item} author={text.author} now={now} />
      </div>
    </li>
  );
}

/**
 * A small cropped hint of the picture. Decorative — the pop-up shows it
 * whole — so a picture that fails to load simply leaves no thumbnail rather
 * than an error squeezed into a small box.
 */
function Thumbnail({ item }: { item: FeedItem }) {
  const { url, failed } = useAnnouncementPicture(item.id, item.imageKey);
  if (!item.imageKey || failed) return null;
  return (
    <span className="board__thumb" aria-hidden="true">
      {url && <img src={url} alt="" />}
    </span>
  );
}

function BoardSkeleton() {
  return (
    <div className="board__skeleton" role="status" aria-label="Loading announcements">
      <span className="board__bone board__bone--tag" />
      <span className="board__bone board__bone--headline" />
      <span className="board__bone" />
      <span className="board__bone board__bone--short" />
    </div>
  );
}

/**
 * Whether the text in `ref` is cut short by its line clamp, so "Read more"
 * appears only when there is more to read. Measured again when the box
 * resizes, since a wider window can fit the whole thing.
 */
function useClipped(ref: RefObject<HTMLElement | null>, content: string | null): boolean {
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setClipped(false);
      return;
    }
    const measure = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Guarded: jsdom and older browsers have no ResizeObserver, and the
    // first measurement still holds there.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, content]);

  return clipped;
}
